'use client';

import { useEffect, useState } from 'react';
import clsx from 'clsx';
import { Divider } from '../divider/Divider';
import { Button } from '../button/Button';
import { Link } from '../link/Link';
import LanguageSwitcher from '@/components/language-switcher/LanguageSwitcher';
import SearchModal from '../modal/variants/SearchModal';
import CookiesConsent from '@/components/cookies-consent/CookiesConsent';
import MobileHeader from '@/components/header/MobileHeader';
import { useTranslations } from '@/lib/locale-context';
import { lockBodyScroll, unlockBodyScroll } from '@/utils/scroll-lock';
import { BaseImage } from '@/components/baseImage/BaseImage';
import { DesktopNavbar } from './desktop/DesktopNavbar';
import { useDropdownNavigation } from './hooks/useDropdownNavigation';
import { useLegacyHashRedirects } from './hooks/useLegacyHashRedirects';
import { useMobileHeaderVisibility } from './hooks/useMobileHeaderVisibility';
import { useStickyNav } from './hooks/useStickyNav';
import { sortNavGroups } from './nav-data';
import type { NavGroup } from './types';

export function Header({ headerData }: { headerData?: NavGroup[] | null }) {
  const { locale, dict } = useTranslations();
  const [isSearchModalOpen, setSearchModalOpen] = useState(false);
  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);

  const groups = sortNavGroups(headerData);
  const dropdown = useDropdownNavigation();
  const { navRowRef, isPinned, navRowHeight } = useStickyNav();
  const [isMobileHeaderVisible, setIsMobileHeaderVisible] =
    useMobileHeaderVisibility();

  useLegacyHashRedirects();

  useEffect(() => {
    if (isMobileMenuOpen) {
      lockBodyScroll();
    } else {
      unlockBodyScroll();
    }

    return unlockBodyScroll;
  }, [isMobileMenuOpen]);

  const closeMenu = () => setMobileMenuOpen(false);

  return (
    <>
      <CookiesConsent />
      {/* That spacer is necessary because the mobile header is fixed, so without the h-20 block the top section would sit underneath the header. */}
      <div className="h-20 lg:hidden" aria-hidden="true" />
      <header
        className={clsx(
          'smzh-header relative top-0 z-50 w-full bg-white transition-transform duration-300 ease-in-out max-lg:fixed max-lg:left-0 max-lg:right-0 max-lg:h-20',
          !isMobileHeaderVisible && 'max-lg:-translate-y-full',
        )}
      >
        {/* Hamburger Menu Button (Mobile) */}
        <div className="container relative mx-auto flex h-full items-center justify-between space-x-6 border-b border-neutral-50 px-4 sm:px-0 lg:hidden">
          <div onClick={closeMenu}>
            {!isMobileMenuOpen ? (
              <Link href={`/${locale}`} className="smzh-logo-blue">
                <BaseImage
                  image={{ url: '/logo/logo-blue.svg' }}
                  width="100"
                  height="100"
                  alt="smzh blue logo"
                  prerender
                />
              </Link>
            ) : (
              <p className="pl-4 text-[20px] font-normal">Menu</p>
            )}
          </div>

          <div className="absolute right-3 flex gap-x-1 md:gap-x-5">
            {!isMobileMenuOpen && (
              <div className="focus-within:ring-blue-500 my-auto flex !h-fit items-center overflow-hidden rounded-md border bg-white transition-all duration-300 ease-in-out focus-within:ring-2">
                <Button
                  leftIcon={{ name: 'search', color: '#07314C' }}
                  variant="tertiary"
                  className="rounded-none !p-2 transition-all ease-in-out hover:bg-neutral-0"
                  onClick={() => setSearchModalOpen(true)}
                />
              </div>
            )}
            <Button
              aria-label="menu"
              name=""
              leftIcon={{
                name: isMobileMenuOpen ? 'x' : 'menu',
                color: '#07314C',
                size: 24,
              }}
              variant="tertiary"
              className="p-1 focus:outline-none"
              onClick={() => {
                setIsMobileHeaderVisible(true);
                setMobileMenuOpen(!isMobileMenuOpen);
              }}
            />
          </div>
        </div>

        {/* Desktop header */}
        <div className="desktop hidden lg:block">
          <section className="firstRow container mx-auto flex items-center justify-between pt-6">
            <Link
              href={`/${locale}`}
              className="smzh-logo-blue h-[25px] w-[100px]"
            >
              <BaseImage
                image={{ url: '/logo/logo-blue.svg' }}
                width="100"
                height="100"
                alt="smzh blue logo"
                prerender
              />
            </Link>
            <div className="languageAndLogin flex items-center space-x-6">
              <LanguageSwitcher currentLocale={locale} />
            </div>
          </section>
          <Divider
            size="small"
            width="full"
            border={true}
            className="!mb-0 !mt-4 border-neutral-50"
          />
          <div id="sticky-trigger" className="h-1"></div>

          {/* To create smooth transition when becomes fixed */}
          {isPinned && <div style={{ height: navRowHeight }} />}

          <section
            ref={navRowRef}
            className={clsx(
              'secondRow w-full border-b duration-300 ease-in-out',
              {
                'fixed left-0 right-0 top-0 z-50 bg-white': isPinned,
                relative: !isPinned,
              },
            )}
          >
            <div className="container relative mx-auto flex min-h-20 items-center justify-between px-4 sm:px-0">
              <DesktopNavbar
                groups={groups}
                locale={locale}
                dropdown={dropdown}
              />

              <section className="absolute right-0 flex space-x-3">
                {/* Search button */}
                <div className="focus-within:ring-blue-500 flex items-center overflow-hidden rounded-md border bg-white transition-all duration-300 ease-in-out focus-within:ring-2">
                  <Button
                    leftIcon={{ name: 'search', color: '#07314C' }}
                    variant="tertiary"
                    className=""
                    onClick={() => setSearchModalOpen(true)}
                  />
                </div>

                <Link
                  title={dict.terminCTA.link.title}
                  href={dict.terminCTA.link.href}
                  variant={dict.terminCTA.link.variant}
                  rightIcon={dict.terminCTA.link.rightIcon}
                  className={dict.terminCTA.link.className}
                />
              </section>
            </div>
          </section>
        </div>

        {/* Mobile Menu — mounted with the page rather than on first tap, so its
            links ship in the served HTML. */}
        <div
          className={clsx(
            'mobile w-full bg-white lg:hidden',
            isMobileMenuOpen ? 'block' : 'hidden',
          )}
          hidden={!isMobileMenuOpen}
          style={{
            height: 'calc(100dvh - 80px - env(safe-area-inset-bottom))',
          }}
        >
          <MobileHeader headerData={groups} closeMenu={closeMenu} />
        </div>

        {/* Search & Form Modals */}
        <SearchModal
          isOpen={isSearchModalOpen}
          onClose={() => setSearchModalOpen(false)}
          ariaLabel="Search modal"
          mainHeadline={{
            title: 'Search modal',
            variant: 'headingThree',
            className: 'text-white',
          }}
        />
      </header>
    </>
  );
}
